/**
 * PrizeTracker Component
 * 
 * Displays individual prizes as clickable badges that can be marked as claimed.
 * Prizes are organized in rows by prize amount in descending order.
 * Each badge shows the prize amount and can be toggled to show a red X when claimed.
 * 
 * @module TabPicker.PrizeTracker
 */

import React, { useMemo } from 'react';
import { useComparisonStore } from '../stores/comparisonStore';
import { cn } from '../utils/cn';
import { MinorCounters } from './MinorCounters';

/**
 * Individual prize badge component
 */
const PrizeBadge: React.FC<{
  gameId: number;
  prizeAmount: string;
  prizeIndex: number;
  isClaimed: boolean;
  onToggle: () => void;
}> = ({ prizeAmount, isClaimed, onToggle }) => {
  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: num % 1 === 0 ? 0 : 2,
    }).format(num);
  };

  return (
    <button
      onClick={onToggle}
      className={cn(
        "relative px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200",
        "hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-1",
        isClaimed
          ? "bg-gray-200 text-gray-500 hover:bg-gray-300 focus:ring-gray-400"
          : "bg-green-100 text-green-800 hover:bg-green-200 focus:ring-green-500"
      )}
      aria-label={`Prize ${formatCurrency(prizeAmount)} - ${isClaimed ? 'Claimed' : 'Available'}`}
      aria-pressed={isClaimed}
    >
      <span className={cn(isClaimed && "opacity-50")}>
        {formatCurrency(prizeAmount)}
      </span>
      
      {/* Red X overlay when claimed */}
      {isClaimed && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <svg
            className="w-full h-full text-red-600"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 6L18 18M6 18L18 6"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}
    </button>
  );
};

/**
 * Prize tier row component - displays all prizes of a specific amount
 */
const PrizeTierRow: React.FC<{
  gameId: number;
  prizeAmount: string;
  quantity: number;
  claimedStatuses: boolean[];
}> = ({ gameId, prizeAmount, quantity, claimedStatuses }) => {
  const { togglePrizeClaim } = useComparisonStore();

  const formatCurrency = (value: string): string => {
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: num % 1 === 0 ? 0 : 2,
    }).format(num);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">
          {formatCurrency(prizeAmount)} prizes
        </span>
        <span className="text-xs text-gray-500">
          ({Array.isArray(claimedStatuses) ? claimedStatuses.filter(c => c).length : 0}/{quantity} claimed)
        </span>
      </div>
      
      {/* Prize badges */}
      <div className="flex flex-wrap gap-2">
        {Array.from({ length: Math.max(0, quantity || 0) }, (_, index) => (
          <PrizeBadge
            key={`${prizeAmount}-${index}`}
            gameId={gameId}
            prizeAmount={prizeAmount}
            prizeIndex={index}
            isClaimed={(Array.isArray(claimedStatuses) && claimedStatuses[index]) || false}
            onToggle={() => togglePrizeClaim(gameId, prizeAmount, index)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Main PrizeTracker component
 */
export const PrizeTracker: React.FC<{
  gameId: number;
  payoutStructure: {
    tickets: Array<{
      amount: string;
      quantity: number;
      isMajor?: boolean; // Optional for backward compatibility
    }>;
  };
}> = ({ gameId, payoutStructure }) => {
  const { config } = useComparisonStore();
  
  // Get deal status for this game with safety checks
  const dealStatus = config?.dealStatuses?.find?.(ds => ds?.gameId === gameId);
  const claimedPrizes = dealStatus?.claimedPrizes || {};
  
  // Split prizes into major and minor, then sort by amount (descending)
  const { majorPrizes, minorPrizes } = useMemo(() => {
    if (!payoutStructure?.tickets || !Array.isArray(payoutStructure.tickets)) {
      return { majorPrizes: [], minorPrizes: [] };
    }
    
    const major = payoutStructure.tickets
      .filter(ticket => ticket.isMajor !== false) // Default to major if not specified (backward compatibility)
      .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
      
    const minor = payoutStructure.tickets
      .filter(ticket => ticket.isMajor === false)
      .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
      
    return { majorPrizes: major, minorPrizes: minor };
  }, [payoutStructure?.tickets]);
  
  // Calculate total claimed prizes (major + minor) with safety checks
  const { totalClaimedMajor, totalClaimedMinor, totalClaimed } = useMemo(() => {
    const claimedMajor = !claimedPrizes || typeof claimedPrizes !== 'object' ? 0 :
      Object.entries(claimedPrizes).reduce((total, [amount, statuses]) => {
        if (!Array.isArray(statuses)) return total;
        return total + statuses.filter(Boolean).length;
      }, 0);
      
    const minorPrizeClaims = dealStatus?.minorPrizeClaims || {};
    const claimedMinor = Object.values(minorPrizeClaims).reduce((total, count) => total + (count || 0), 0);
    
    return {
      totalClaimedMajor: claimedMajor,
      totalClaimedMinor: claimedMinor,
      totalClaimed: claimedMajor + claimedMinor
    };
  }, [claimedPrizes, dealStatus?.minorPrizeClaims]);
  
  const { totalMajorPrizes, totalMinorPrizes, totalPrizes } = useMemo(() => {
    const majorCount = majorPrizes.reduce((total, tier) => total + tier.quantity, 0);
    const minorCount = minorPrizes.reduce((total, tier) => total + tier.quantity, 0);
    
    return {
      totalMajorPrizes: majorCount,
      totalMinorPrizes: minorCount,
      totalPrizes: majorCount + minorCount
    };
  }, [majorPrizes, minorPrizes]);

  // Early return if no valid payout structure
  if (!payoutStructure?.tickets || !Array.isArray(payoutStructure.tickets) || payoutStructure.tickets.length === 0) {
    return (
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="text-sm text-gray-500 italic">
          No prize data available for this game
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="space-y-2">
          <div className="text-sm">
            <span className="font-medium text-gray-700">Total Prizes Claimed: </span>
            <span className={cn(
              "font-bold",
              totalClaimed === 0 ? "text-green-600" : 
              totalClaimed === totalPrizes ? "text-red-600" : 
              "text-orange-600"
            )}>
              {totalClaimed} / {totalPrizes}
            </span>
          </div>
          
          {/* Breakdown by type */}
          {(totalMajorPrizes > 0 || totalMinorPrizes > 0) && (
            <div className="text-xs text-gray-600 space-y-1">
              {totalMajorPrizes > 0 && (
                <div>Major prizes: {totalClaimedMajor} / {totalMajorPrizes} claimed</div>
              )}
              {totalMinorPrizes > 0 && (
                <div>Minor prizes: {totalClaimedMinor} / {totalMinorPrizes} claimed</div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Major Prizes Section */}
      {majorPrizes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Major Prizes</h3>
            <span className="text-sm text-gray-500">(tracked individually)</span>
          </div>
          
          <div className="space-y-4">
            {majorPrizes.map((tier) => {
              if (!tier || typeof tier.amount !== 'string' || typeof tier.quantity !== 'number') {
                return null;
              }
              
              const existingStatuses = claimedPrizes?.[tier.amount];
              const claimedStatuses = Array.isArray(existingStatuses) 
                ? [...existingStatuses] 
                : Array(tier.quantity).fill(false);
              
              // Ensure array is correct length
              while (claimedStatuses.length < tier.quantity) {
                claimedStatuses.push(false);
              }
              if (claimedStatuses.length > tier.quantity) {
                claimedStatuses.splice(tier.quantity);
              }
              
              return (
                <PrizeTierRow
                  key={tier.amount}
                  gameId={gameId}
                  prizeAmount={tier.amount}
                  quantity={tier.quantity}
                  claimedStatuses={claimedStatuses}
                />
              );
            })}
          </div>
        </div>
      )}
      
      {/* Minor Prizes Section */}
      {minorPrizes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">Minor Prizes</h3>
            <span className="text-sm text-gray-500">(tracked with counters)</span>
          </div>
          
          <MinorCounters
            gameId={gameId}
            minorPrizes={minorPrizes.map(tier => ({
              amount: tier.amount,
              quantity: tier.quantity
            }))}
          />
        </div>
      )}
      
      {/* No prizes message */}
      {majorPrizes.length === 0 && minorPrizes.length === 0 && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500 italic">
            No prize data available for this game
          </div>
        </div>
      )}
    </div>
  );
};